import React from 'react';

import { ReactComponent as Facebook } from '../../media/images/facebook-circle.svg';
import { ReactComponent as Youtube } from '../../media/images/youtube-circle.svg';
import { ReactComponent as LinkedIn } from '../../media/images/linkedin-circle.svg';
import { ReactComponent as Instagram } from '../../media/images/instagram-circle.svg';
import GooglePlaySrc from '../../media/images/GooglePlay.png';
import AppStoreSrc from '../../media/images/AppStore.png';

import { createStyles } from 'utils/createStyle';
import { Routes } from 'routes';
import { ApplePlayStoreUrl, ExternalLinks, GooglePlayStoreUrl } from 'externalLinks';
import { BB_IMAGE_URL, BB_URL, Branding, IS_FLIPGIVE } from 'common/env';

const styles = createStyles({
    extraSmallText: ['font-light text-white', { fontSize: 12, lineHeight: '16px' }],
});

interface FooterLinkItem {
    value: string;
    href: string;
    openInNewTab?: boolean;
}

const raiseRightFooterBrands: FooterLinkItem[] = [
    { value: 'Amazon.com', href: '/brands/48-amazon' },
    { value: 'Best Buy', href: '/brands/81-best-buy' },
    { value: 'The Home Depot', href: '/brands/300-home-depot' },
    { value: 'Starbucks', href: '/brands/650-starbucks' },
    { value: 'Target', href: '/brands/684-target' },
    { value: 'Walmart', href: '/brands/707-walmart' },
];

// Absolute PDP URLs that open in the same tab.
const flipGiveFooterBrands: FooterLinkItem[] = [
    { value: 'Walmart', href: 'https://www.flipgive.com/brands/1158-walmart-ca/gift-cards' },
    {
        value: 'Lululemon',
        href: 'https://www.flipgive.com/brands/affiliate-479042-lululemon/shop-online',
    },
    { value: 'Sephora', href: 'https://www.flipgive.com/brands/1136-sephora-ca/gift-cards' },
    {
        value: 'Old Navy',
        href: 'https://www.flipgive.com/brands/1165-old-navy-ca/gift-cards',
    },
    { value: 'Starbucks', href: 'https://www.flipgive.com/brands/1142-starbucks-ca/gift-cards' },
    {
        value: 'Under Armour',
        href: 'https://www.flipgive.com/brands/affiliate-38-under-armour/shop-online',
    },
];

const footerLinks: { sectionName: string; items: FooterLinkItem[] }[] = [
    {
        sectionName: 'Fundraising Info',
        items: [
            { value: 'Gift Card Fundraising', href: Routes.HowItWorksGiftCards },
            { value: 'Shop Online', href: Routes.HowItWorksShopOnline },
            ...(IS_FLIPGIVE ? [] : [{ value: 'Local Dining', href: Routes.HowItWorksLocalDining }]),
            // FEC-2567: Hide Book Travel in footer Fundraising Info for FlipGive. Remove this guard to restore.
            ...(IS_FLIPGIVE ? [] : [{ value: 'Book Travel', href: Routes.HowItWorksBookTravel }]),
            {
                value: 'Estimate Your Earnings',
                href: Routes.HowItWorksEstimateCalculator,
            },
            { value: 'Coordinator Resources', href: Routes.SupportOrganization },
            { value: 'Participant Resources', href: Routes.SupportParticipant },
            {
                value: 'Fundraising Ideas',
                href: IS_FLIPGIVE
                    ? 'https://www.flipgive.com/stories/fundraising-ideas'
                    : Routes.FundraisingIdeas,
            },
            {
                value: 'Fundraising Strategy',
                href: Routes.FundraisingStrategy,
            },
            ...(IS_FLIPGIVE ? [] : [{ value: 'School Choice', href: Routes.SupportSchoolChoice }]),
            { value: 'Blog', href: IS_FLIPGIVE ? 'https://www.flipgive.com/stories' : Routes.Blog },
            {
                value: IS_FLIPGIVE ? 'Team Spotlight' : 'Impact Stories',
                href: IS_FLIPGIVE
                    ? 'https://www.flipgive.com/about-us/team-spotlight'
                    : Routes.ImpactStories,
            },
        ],
    },
    {
        sectionName: 'Brands',
        items: [
            // NOTE: These ids point to production brand ids and not other environments
            // NOTE: Will need to discuss how to properly get these brands programmatically
            // in the future.
            // FEC-2652: FlipGive uses its own popular-brand list with absolute PDP URLs (same tab).
            ...(IS_FLIPGIVE ? flipGiveFooterBrands : raiseRightFooterBrands),
            { value: 'See All Brands', href: Routes.Brands },
            { value: 'Shop By Category', href: Routes.Shop },
        ],
    },
    {
        sectionName: 'Company',
        items: [
            { value: 'About Us', href: Routes.AboutUs },
            { value: 'Who We Help', href: Routes.WhoWeHelp },
            {
                value: IS_FLIPGIVE ? 'Help Centre' : 'Help Center',
                href: ExternalLinks.HelpCenterFooter,
                openInNewTab: true,
            },
            { value: 'Contact Us', href: Routes.Contact },
            // FEC-2722: Hide Careers in footer Company for FlipGive. Remove this guard to restore.
            ...(IS_FLIPGIVE ? [] : [{ value: 'Careers', href: Routes.Careers }]),
            { value: 'Privacy Policy', href: Routes.PrivacyPolicy },
            { value: 'Terms of Use', href: Routes.TermsOfUse },
            {
                value: 'Sitemap',
                href: IS_FLIPGIVE ? 'https://www.flipgive.com/m/sitemap-flipgive' : Routes.Sitemap,
            },
        ],
    },
];

const Divider = () => (
    <div style={{ height: 5 }} className="my-2 bg-brand-accent w-10 rounded-sm m-auto lg:mx-0" />
);

export const SiteFooter = () => (
    <footer className="text-white bg-brand-dark p-10 lg:px-32">
        <div className="grid grid-cols-1 text-center md:grid-cols-2 lg:grid-cols-4 lg:text-left">
            {footerLinks.map((linkSection) => (
                <section className="flex flex-col" key={linkSection.sectionName}>
                    <h5 className="text-lg font-bold">{linkSection.sectionName}</h5>
                    <Divider />
                    {linkSection.items.map((item, idx) => {
                        const isLastChild = linkSection.items.length - 1 === idx;
                        const bottomMargin = isLastChild ? 'mb-10' : 'mb-1';
                        const href = item.href;
                        return (
                            <a
                                key={item.value}
                                className={`no-underline text-sm font-light text-white mt-1 ${bottomMargin} lg:mb-1`}
                                href={href}
                                {...(item.openInNewTab && {
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                })}
                            >
                                {item.value}
                            </a>
                        );
                    })}
                </section>
            ))}
            <section className="flex flex-col justify-center lg:justify-start">
                <h5 className="text-lg font-bold">Shop On Our App</h5>
                <Divider />
                <a
                    className="mx-auto mt-1 mb-2 lg:items-start lg:mx-0"
                    href={ApplePlayStoreUrl}
                    target="_blank"
                >
                    <img src={AppStoreSrc} />
                </a>
                <a className="mx-auto lg:mx-0" href={GooglePlayStoreUrl} target="_blank">
                    <img src={GooglePlaySrc} />
                </a>
                <div className="mt-5">
                    <a target="_blank" href={ExternalLinks.Facebook}>
                        <Facebook className="inline-block mr-4" />
                    </a>
                    <a target="_blank" href={ExternalLinks.LinkedIn}>
                        <LinkedIn className="inline-block mr-4" />
                    </a>
                    <a target="_blank" href={ExternalLinks.Youtube}>
                        <Youtube className="inline-block mr-4" />
                    </a>
                    <a target="_blank" href={ExternalLinks.Instagram}>
                        <Instagram className="inline-block" />
                    </a>
                </div>
            </section>
        </div>
        <div className="flex flex-col md:flex-row gap-6 mt-8">
            <div className="self-center" style={{ minWidth: '141px' }}>
                <a target="_blank" href={BB_URL}>
                    <img src={BB_IMAGE_URL} />
                </a>
            </div>
            <div>
                {IS_FLIPGIVE ? (
                    <p className={`mb-2 ${styles.extraSmallText}`}>
                        COPYRIGHT © {new Date().getFullYear()} FLIPGIVE CORPORATION, A RAISERIGHT
                        COMPANY. ALL RIGHTS RESERVED.
                    </p>
                ) : (
                    <p className={`mb-2 ${styles.extraSmallText}`}>
                        COPYRIGHT © 1999 - {new Date().getFullYear()} RAISERIGHT. ALL RIGHTS
                        RESERVED.
                    </p>
                )}
                <p className={styles.extraSmallText}>
                    The merchants represented are not sponsors of {Branding} or otherwise affiliated
                    with {Branding}. The Logos and other identifying marks on this website are
                    trademarks of and owned by each represented company and/or its affiliates.
                    Please visit each company's website for additional terms and conditions.
                </p>
            </div>
        </div>
    </footer>
);
